import { type CompanyRole, companyRoleEnum, userTypeEnum } from "kubb";

// The roles used in UI authorization checks span BOTH generated enums: the
// global `userTypeEnum.superadmin` plus the per-company `companyRoleEnum` roles
// (excluding "none"). Building the hierarchy here from the enums — with computed
// keys — means this file holds zero raw role-string literals and is the single
// place role values are named. The ratchet test
// (lib/roleStringLiteral.ratchet.test.ts) blocks new raw role literals elsewhere;
// new role checks should reference the enums or this module.
export type AuthRole =
  | typeof userTypeEnum.superadmin
  | Exclude<CompanyRole, typeof companyRoleEnum.none>;

const { superadmin } = userTypeEnum;
const { admin, editor, viewer } = companyRoleEnum;

// A user holding role X is granted everything in ROLE_HIERARCHY[X]. Mirrors the
// previous DisplayIfUserType switch exactly: superadmin ⊇ admin ⊇ editor ⊇ viewer.
export const ROLE_HIERARCHY: Record<AuthRole, AuthRole[]> = {
  [superadmin]: [superadmin],
  [admin]: [admin, superadmin],
  [editor]: [superadmin, admin, editor],
  [viewer]: [superadmin, admin, editor, viewer],
};

// Expand a requested role into the set that satisfies it. Guard with
// Array.isArray so a role string that collides with an Object.prototype member
// (e.g. "toString"/"constructor") falls back to the full chain instead of
// returning an inherited property — identical to the old switch's `default`
// branch for any unknown role.
export const expandRole = (role: string): AuthRole[] => {
  const chain = ROLE_HIERARCHY[role as AuthRole];
  return Array.isArray(chain) ? chain : ROLE_HIERARCHY[viewer];
};
